import { t, type I18nLocale } from '@ardoq/i18n';
import {
  type AvailableReferenceFieldOptionsLabels,
  type BubbleChartZoomControlsLabels,
  type ComponentTypeSelectLabels,
  type QuadrantCustomizerLabels,
  type SelectedQuadrantDropdownLabels,
  type TimeLayoutLabels,
} from '@ardoq/bubble-chart';

export const getQuadrantCustomizerLabels = (
  i18nLocale: I18nLocale
): QuadrantCustomizerLabels => ({
  headerText: t(
    i18nLocale,
    'bubbleChart:quadrantCustomizer.headerText',
    'Custom Quadrants'
  ),
  cancelButtonText: t(
    i18nLocale,
    'bubbleChart:quadrantCustomizer.cancelButtonText',
    'Cancel'
  ),
  applyButtonText: t(
    i18nLocale,
    'bubbleChart:quadrantCustomizer.applyButtonText',
    'Apply'
  ),
  quadrantLabelsTitle: t(
    i18nLocale,
    'bubbleChart:quadrantCustomizer.quadrantLabelsTitle',
    'Quadrant Labels'
  ),
  xMinimumLabel: t(
    i18nLocale,
    'bubbleChart:quadrantCustomizer.xMinimumLabel',
    'X Minimum:'
  ),
  xMaximumLabel: t(
    i18nLocale,
    'bubbleChart:quadrantCustomizer.xMaximumLabel',
    'X Maximum:'
  ),
  yMinimumLabel: t(
    i18nLocale,
    'bubbleChart:quadrantCustomizer.yMinimumLabel',
    'Y Minimum:'
  ),
  yMaximumLabel: t(
    i18nLocale,
    'bubbleChart:quadrantCustomizer.yMaximumLabel',
    'Y Maximum:'
  ),
  autoPlaceholder: t(
    i18nLocale,
    'bubbleChart:quadrantCustomizer.autoPlaceholder',
    'Auto'
  ),
});

export const getBubbleChartZoomControlsLabels = (
  i18nLocale: I18nLocale
): BubbleChartZoomControlsLabels => ({
  zoomInLabel: t(i18nLocale, 'bubbleChart:zoomControls.zoomIn', 'Zoom in'),
  zoomOutLabel: t(i18nLocale, 'bubbleChart:zoomControls.zoomOut', 'Zoom out'),
  resetZoomLabel: t(
    i18nLocale,
    'bubbleChart:zoomControls.resetZoom',
    'Reset zoom'
  ),
});

export const getSelectedQuadrantDropdownLabels = (
  i18nLocale: I18nLocale
): SelectedQuadrantDropdownLabels => ({
  noBackgroundSetUpLabel: t(
    i18nLocale,
    'bubbleChart:selectedQuadrant.noBackgroundSetUp',
    'No background set up'
  ),
  zoomToQuadrantLabel: t(
    i18nLocale,
    'bubbleChart:selectedQuadrant.zoomToQuadrant',
    'Zoom to quadrant'
  ),
  showAllLabel: t(
    i18nLocale,
    'bubbleChart:selectedQuadrant.showAll',
    'Show all'
  ),
  zoomToTemplate: (quadrantLabel: string) =>
    t(
      i18nLocale,
      'bubbleChart:selectedQuadrant.zoomTo',
      { label: quadrantLabel },
      'Zoom to {{label}}'
    ),
});

export const getComponentTypeSelectLabels = (
  i18nLocale: I18nLocale
): ComponentTypeSelectLabels => ({
  allLabel: t(i18nLocale, 'bubbleChart:componentTypeSelect.all', 'All'),
  componentTypeLabel: t(
    i18nLocale,
    'bubbleChart:componentTypeSelect.componentType',
    'Components as bubbles'
  ),
});

export const getTimeLayoutLabels = (
  i18nLocale: I18nLocale
): TimeLayoutLabels => ({
  tolerateLabel: t(i18nLocale, 'bubbleChart:timeQuadrant.tolerate', 'tolerate'),
  investLabel: t(i18nLocale, 'bubbleChart:timeQuadrant.invest', 'invest'),
  migrateLabel: t(i18nLocale, 'bubbleChart:timeQuadrant.migrate', 'migrate'),
  eliminateLabel: t(
    i18nLocale,
    'bubbleChart:timeQuadrant.eliminate',
    'eliminate'
  ),
});

export const getAvailableReferenceFieldOptionsLabels = (
  i18nLocale: I18nLocale
): AvailableReferenceFieldOptionsLabels => ({
  referenceFieldsHeader: t(
    i18nLocale,
    'bubbleChart:referenceFieldOptions.referenceFields',
    'Reference fields'
  ),
  referencedComponentFieldsHeader: t(
    i18nLocale,
    'bubbleChart:referenceFieldOptions.referencedComponentFields',
    'Referenced component fields'
  ),
  bubbleComponentFieldsHeader: t(
    i18nLocale,
    'bubbleChart:referenceFieldOptions.sourceOfBubblesFields',
    'Source of bubbles fields'
  ),
});

export const getViewpointModeReferenceFieldOptionsLabels = (
  i18nLocale: I18nLocale
): AvailableReferenceFieldOptionsLabels => ({
  ...getAvailableReferenceFieldOptionsLabels(i18nLocale),
  referencedComponentFieldsHeader: t(
    i18nLocale,
    'bubbleChart:referenceFieldOptions.linkedComponentFields',
    'Linked component fields'
  ),
});
